import "./index.css";

import { ErrorCatcher, RegionsLoader } from "@neru/base/components";
import { setupStore } from "@neru/base/modules";
import { componentLoader, getIsProxy, Revision } from "@neru/base/utils";
import { IconsSprite, Spinner } from "@neru/ui";
import React, { lazy, Suspense } from "react";
import ReactGA from "react-ga4";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { theme } from "./styles";
import { setProfileInfo } from "./utils/AdobeAnalytics";
import { config } from "./utils/config";

interface BootstrapProps {
  userInfo?: MFA.UserInfo;
}

Revision.setRevision("market");

if (import.meta.env.VITE_GA_ID) {
  ReactGA.initialize(import.meta.env.VITE_GA_ID);
}

const getAccountId = (userInfo?: MFA.UserInfo) => {
  if (!userInfo || userInfo?.status !== "logged-in") return;

  const id = localStorage.getItem("vch.account_id");

  if (id && userInfo.apiKeys.find(({ apiKey }) => apiKey === id)) {
    return id;
  }

  return userInfo.apiKeys.find(({ master }) => master)?.apiKey;
};

const App = lazy(() => componentLoader(() => import("./App")));

const Bootstrap = ({ userInfo }: BootstrapProps) => {
  // Production is MFA and uses IdentityAwareProxy by default
  const WithAuth =
    config.isMFA && getIsProxy()
      ? lazy(() => componentLoader(() => import("./auth/IdentityAwareProxy")))
      : lazy(() => componentLoader(() => import("./auth/OpenIDConnect")));

  if (userInfo?.status === "pending") {
    return <Spinner />;
  }

  const accountId = getAccountId(userInfo);

  if (userInfo) {
    if (userInfo.status === "logged-in" && accountId) {
      setProfileInfo({ ...userInfo, accountId });
    } else {
      setProfileInfo({ status: "logged-out" });
      localStorage.removeItem("vch.auth");
    }
  }

  return (
    <React.StrictMode>
      <IconsSprite />
      <ThemeProvider theme={theme}>
        <ErrorCatcher apiKey={import.meta.env.VITE_BUGSNAG_API_KEY}>
          <Provider store={setupStore()}>
            <RegionsLoader>
              <Suspense fallback={<Spinner />}>
                <Router basename={config.routerBaseName}>
                  <WithAuth accountId={accountId}>
                    <App hasPortalSession={userInfo?.status === "logged-in"} />
                  </WithAuth>
                </Router>
              </Suspense>
            </RegionsLoader>
          </Provider>
        </ErrorCatcher>
      </ThemeProvider>
    </React.StrictMode>
  );
};

export default Bootstrap;
